import Bugsnag, { NotifiableError } from '@bugsnag/js'
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useFetchUserOffersForPTQuery } from 'api/productTemplateListsApi'
import { useLazyGetPricesQuery } from 'api/productVariantsApi'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import { mockProductTemplate } from 'productTemplate/components/__mocks__/__data__/mockProductTemplate'
import { IOrderInfo } from 'productTemplate/types/IOrderInfo'
import { SizePickerOption, SizePickerVariant } from 'productTemplate/types/ProductVariant'
import { getProductVariantInfo } from 'server/utils/productTemplate/getProductVariantInfo'
import { isConditional } from 'server/utils/productTemplate/productVariantUtils'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { useUser } from 'shared/hooks/useUser'
import { ProductTemplateProp } from 'shared/types'
import { IProductSizeOption } from 'shared/types/IProductSizeOption'
import { PTListsProductTemplate } from 'shared/types/ProductTemplateLists'
import { isomorphicLog } from 'shared/utils/logging-utils'

export type ModalType = 'new' | undefined

export type NewProductViewType = 'ship' | 'pickup'

export type ProductTemplateContextState = {
  activeProductModal: ModalType
  isApparel: boolean
  loadingPricing: boolean
  newProductView: NewProductViewType
  orderInfo?: IOrderInfo
  productTemplate: ProductTemplateProp
  selectedConditionalProduct?: IProductSizeOption
  selectedInstantShip: boolean
  selectedSize?: SizePickerOption
  setActiveProductModal(activeModal?: ModalType): void
  setInstantShip(selectedInstantShip: boolean): void
  setLowPriceConditionalProduct(conditionalProduct?: IProductSizeOption): void
  setSelectedConditionalProduct(conditionalProduct?: IProductSizeOption): void
  setSize(size: SizePickerOption): void
  setNewProductView: Dispatch<SetStateAction<NewProductViewType>>
  userOffersForPT?: { sizes: number[]; productTemplate: PTListsProductTemplate }
}

type ProductTemplateContextProviderProps = {
  children: ReactNode
  productTemplate: ProductTemplateProp
  urlSize?: string | null
}

// initialize any values the context needs here
export const ProductTemplateContext = createContext<ProductTemplateContextState>({
  activeProductModal: undefined,
  isApparel: false,
  loadingPricing: false,
  newProductView: 'ship',
  // We use a mock to be able to export useProductTemplateContext
  // while assuring that downstream components understand
  // productTemplate on the context is guaranteed
  // but this mock will never surface (check the test suite to verify)
  productTemplate: mockProductTemplate,
  selectedInstantShip: true,
  setActiveProductModal: () => null,
  setInstantShip: () => null,
  setLowPriceConditionalProduct: () => null,
  setNewProductView: () => null,
  setSelectedConditionalProduct: () => null,
  setSize: () => null,
  userOffersForPT: { sizes: [], productTemplate: {} as PTListsProductTemplate },
})

export const useProductTemplateContext = (): ProductTemplateContextState =>
  useContext(ProductTemplateContext)

export const ProductTemplateContextProvider = ({
  children,
  productTemplate: productTemplateProp,
  urlSize,
}: ProductTemplateContextProviderProps) => {
  const [productTemplate, setProductTemplate] = useState(productTemplateProp)
  const defaultSizing = getDefaultSize(productTemplateProp, urlSize)
  const [triggerGetPrices, { data: pricingData }] = useLazyGetPricesQuery()
  const {
    currencyCode: selectedCurrencyCode,
    country: selectedCountryCode,
    availableCurrencies,
  } = useShoppingRegionContext()
  const hideHighPricedUsedProduct = useFeatureFlag(FeatureFlag.HIDE_HIGH_PRICED_USED_PRODUCT)
  const isOffersEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_ENABLE_OFFERS)
  const marketPricingEnabled = useFeatureFlag(FeatureFlag.MARKET_PRICING)
  const [activeProductModal, setModal] = useState<ModalType>()
  const [isOfferSizeSet, setIsOfferSizeSet] = useState(false)
  const [loadingPricing, setLoadingPricing] = useState(false)
  const [newProductView, setNewProductView] = useState<NewProductViewType>('ship')
  const [prevCountry, setPrevCountry] = useState(selectedCountryCode)
  const [prevCurrency, setPrevCurrency] = useState(selectedCurrencyCode)
  const [selectedConditionalProduct, setSelectedConditionalProduct] = useState<IProductSizeOption>()
  const [selectedInstantShip, setInstantShip] = useState(false)
  const [selectedSize, setSize] = useState(defaultSizing || productTemplate.sizePickerDefault)
  const { isAuthenticated, currentUser } = useUser()
  const isApparel = productTemplate.productCategory !== 'shoes'
  const { data: userOffersForPT, isSuccess: isUserOffersForPTSuccess } =
    useFetchUserOffersForPTQuery(
      {
        productTemplateId: productTemplateProp.id,
        currentUserId: currentUser?.id,
      },
      { skip: !isAuthenticated || !isOffersEnabled, refetchOnMountOrArgChange: true },
    )

  const getOrderInfo = () => {
    if (selectedConditionalProduct) {
      return {
        boxCondition: selectedConditionalProduct.productBoxCondition,
        condition: selectedConditionalProduct.shoeCondition,
        details: productTemplate.details,
        expectedPriceCents: selectedConditionalProduct.price.value,
        instantShip: selectedConditionalProduct.isInstantShip,
        localizedExpectedPriceCents: selectedConditionalProduct.price.localizedValue,
        mainPictureUrl: productTemplate.pictureUrl,
        name: productTemplate.name,
        productCategory: productTemplate?.productCategory,
        productId: selectedConditionalProduct.productId,
        productTemplateId: Number(productTemplate.id),
        productType: 'Conditional',
        size: selectedConditionalProduct.size.value,
        sizeDisplay: selectedConditionalProduct.size.display,
      }
    }
    if (selectedSize && activeProductModal) {
      const selectedProduct: SizePickerVariant = selectedSize[activeProductModal]!
      const priceType = selectedInstantShip ? 'instantShipLowestPriceCents' : 'lowestPriceCents'
      if (activeProductModal === 'new' && selectedProduct.shoeCondition !== 'new_no_defects') {
        return null
      }
      return {
        boxCondition: selectedProduct.boxCondition,
        condition: selectedProduct.shoeCondition,
        details: productTemplate.details,
        expectedPriceCents: selectedProduct[priceType]!.value,
        instantShip: selectedInstantShip,
        localizedExpectedPriceCents: selectedProduct[priceType]!.localizedValue,
        mainPictureUrl: productTemplate.pictureUrl,
        name: productTemplate.name,
        productCategory: productTemplate?.productCategory,
        productId: null,
        productTemplateId: Number(productTemplate.id),
        productType: activeProductModal,
        size: selectedSize.size.value,
        sizeDisplay: selectedSize.size.display,
      }
    }
  }

  const setActiveProductModal = (activeModal: ModalType) => {
    setModal(activeModal)
    const isInstantShip =
      activeModal && selectedSize
        ? (selectedSize[activeModal]?.instantShipLowestPriceCents?.value || Infinity) <=
          (selectedSize[activeModal]?.lowestPriceCents?.value || Infinity)
        : false
    setInstantShip(isInstantShip)
    setSelectedConditionalProduct(undefined)
    if (activeModal) {
      Bugsnag.leaveBreadcrumb(`User clicked buy ${activeModal} button in buy button row`, {
        slug: productTemplate.slug,
        size: selectedSize && selectedSize.size.display,
      })
      try {
        sendTrackingEvent(
          `PDP_BUY_${activeModal.toUpperCase()}_TAP` as 'PDP_BUY_NEW_TAP' | 'PDP_BUY_USED_TAP',
          {
            product_template_slug: productTemplate.slug,
          },
        )
      } catch (e) {
        isomorphicLog(`Error sending pdp buy ${activeModal} tap: ${JSON.stringify(e)}`)
      }
    }
  }

  const setLowPriceConditionalProduct = (lowPriceConditionalProduct: IProductSizeOption) => {
    if (!selectedConditionalProduct && isConditional(selectedSize)) {
      setSelectedConditionalProduct(lowPriceConditionalProduct)
    }
  }

  const updateProductTemplate = (pt: ProductTemplateProp) => {
    setProductTemplate(pt)
    const defaultSizing = getDefaultSize(pt, urlSize)
    if (!isOfferSizeSet) {
      setSize(defaultSizing || pt.sizePickerDefault)
    }
  }

  // pre-select the size to the lowest size in the user's offers (if any) for this PT
  useEffect(() => {
    if (isUserOffersForPTSuccess && !isOfferSizeSet) {
      const offerSelectedSize = productTemplate?.sizePickerOptions?.find(
        (shoe) => shoe?.size?.value === userOffersForPT?.sizes?.[0],
      )
      if (offerSelectedSize && offerSelectedSize.size.value !== selectedSize?.size.value) {
        setSize(offerSelectedSize)
        setIsOfferSizeSet(true) // only pre-select the size once
      }
    }
  }, [isUserOffersForPTSuccess, isOfferSizeSet, productTemplate, userOffersForPT, selectedSize])

  // fetch prices when the component mounts or when the selected country or currency changes
  useEffect(() => {
    const getPrices = async () => {
      try {
        const pricingVariantsData = await triggerGetPrices({
          countryCode: selectedCountryCode,
          productTemplateId: productTemplateProp.slug,
        }).unwrap()

        const variantInfo = getProductVariantInfo({
          disambiguatedCurrencySymbol:
            availableCurrencies.find((c) => c.isoCode === selectedCurrencyCode)
              ?.disambiguatedSymbol || '',
          hideHighPricedUsedProduct,
          marketPricingEnabled,
          productTemplateId: productTemplateProp.id,
          productVariants: pricingVariantsData,
        })

        updateProductTemplate(Object.assign({}, productTemplate, variantInfo))
        setLoadingPricing(false)
      } catch (error) {
        // Skip sending cloudflare 403 errors to bugsnag
        if (typeof error === 'object' && error !== null && 'status' in error && (error as any).status === 403) return

        Bugsnag.notify(error as NotifiableError, (event) => {
          event.addMetadata('ProductTemplateContextProvider.getPrices', {
            productTemplateId: productTemplateProp.id,
            countryCode: selectedCountryCode,
            currencyCode: selectedCurrencyCode,
          })
        })
      }
    }

    if (
      (productTemplateProp.fetchedServerSide && !pricingData) ||
      selectedCurrencyCode !== prevCurrency ||
      selectedCountryCode !== prevCountry
    ) {
      setLoadingPricing(true)
      getPrices()

      if (selectedCurrencyCode !== prevCurrency) {
        setPrevCurrency(selectedCurrencyCode)
      }
      if (selectedCountryCode !== prevCountry) {
        setPrevCountry(selectedCountryCode)
      }
    }
  }, [selectedCurrencyCode, selectedCountryCode])

  useEffect(() => {
    if (productTemplate?.id !== productTemplateProp?.id) {
      setIsOfferSizeSet(false)
      updateProductTemplate(productTemplateProp)
    }
  }, [productTemplateProp?.id])

  const orderInfo = getOrderInfo()
  const state = useMemo<ProductTemplateContextState>(
    () => ({
      activeProductModal,
      isApparel,
      loadingPricing,
      newProductView,
      orderInfo,
      productTemplate,
      selectedConditionalProduct,
      selectedInstantShip,
      selectedSize,
      setActiveProductModal,
      setInstantShip,
      setLowPriceConditionalProduct,
      setSelectedConditionalProduct,
      setSize,
      setNewProductView,
      userOffersForPT,
    }),
    [
      activeProductModal,
      isApparel,
      loadingPricing,
      newProductView,
      orderInfo,
      productTemplate,
      selectedConditionalProduct,
      selectedInstantShip,
      selectedSize,
      setActiveProductModal,
      setInstantShip,
      setLowPriceConditionalProduct,
      setSelectedConditionalProduct,
      setSize,
      setNewProductView,
      userOffersForPT,
    ],
  )

  return <ProductTemplateContext.Provider value={state}>{children}</ProductTemplateContext.Provider>
}


const getDefaultSize = (productTemplate: ProductTemplateProp, urlSize?: string | null) => {
  return productTemplate?.sizePickerOptions?.find(
    (shoe) => shoe?.size?.display === urlSize && shoe.new,
  )
}
